/**
 * Public status page — no JWT required. Accessed via /status/:token.
 * Optionally password-gated.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Logo from "@/components/Logo";
import StatusBadge from "@/components/StatusBadge";
import { HardDrives, Pulse, ArrowsClockwise, ShieldCheck } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PublicStatus() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [password, setPassword] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadMeta = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${BASE}/public-status/${token}/meta`);
      setMeta(data);
      if (!data.has_password) {
        await loadData("");
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const loadData = async (pw) => {
    setSubmitting(true); setError("");
    try {
      const { data } = await axios.post(`${BASE}/public-status/${token}`, { password: pw || null });
      setData(data);
    } catch (e) {
      setData(null);
      setError(e.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  useEffect(() => { loadMeta(); }, [token]);

  if (loading) {
    return <FullCenter><div className="text-xs text-slate-400">loading…</div></FullCenter>;
  }
  if (!meta) {
    return <FullCenter>
      <div className="text-center">
        <h1 className="font-display text-2xl font-bold text-white mb-2">Status page not found</h1>
        <p className="text-sm text-slate-400">This URL is invalid, expired, or has been disabled.</p>
      </div>
    </FullCenter>;
  }

  // Password gate
  if (meta.has_password && !data) {
    return (
      <FullCenter>
        <div className="sw-card p-7 w-full max-w-sm">
          <div className="mb-6"><Logo /></div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-cyan mb-1">Status page</div>
          <h1 className="font-display text-xl font-bold tracking-tight text-white mb-1">{meta.name}</h1>
          <p className="text-xs text-slate-400 mb-5 inline-flex items-center gap-1.5">
            <ShieldCheck size={14} weight="duotone" /> Password protected
          </p>
          <form onSubmit={(e) => { e.preventDefault(); loadData(password); }} className="space-y-3" data-testid="status-password-form">
            <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required
              data-testid="status-password-input"
              className="w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan" />
            {error && <div className="text-xs text-red-400">{error}</div>}
            <button type="submit" disabled={submitting} data-testid="status-unlock-btn"
              className="w-full bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 disabled:opacity-60 rounded-md px-4 py-2 text-sm font-semibold">
              {submitting ? "Unlocking…" : "Unlock"}
            </button>
          </form>
        </div>
      </FullCenter>
    );
  }

  // Data view
  if (error) return <FullCenter><div className="text-sm text-red-400">{error}</div></FullCenter>;
  if (!data) return <FullCenter><div className="text-xs text-slate-400">loading data…</div></FullCenter>;

  const allBackups = data.backups || [];
  const allMons = data.monitors || [];
  const isProblem = (it) => ["failure", "warning", "missed", "down", "degraded"].includes(it.status);
  const problems = [
    ...allBackups.map(x => ({ ...x, _kind: "backup" })),
    ...allMons.map(x => ({ ...x, _kind: "monitor" })),
  ].filter(isProblem);
  const healthy = [
    ...allBackups.map(x => ({ ...x, _kind: "backup" })),
    ...allMons.map(x => ({ ...x, _kind: "monitor" })),
  ].filter((x) => !isProblem(x));

  return (
    <div className="min-h-screen bg-background-deep text-white sw-grid-bg">
      <header className="border-b border-border bg-background-deep/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto h-14 px-6 flex items-center justify-between">
          <Logo />
          <button onClick={() => loadData(password)} disabled={submitting} data-testid="status-refresh-btn"
            className="text-xs inline-flex items-center gap-1.5 text-slate-300 hover:text-brand-cyan">
            <ArrowsClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-cyan mb-1">SERVICE STATUS</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white mb-2">{data.client_name}</h1>
          <p className="text-xs text-slate-500">updated {new Date(data.generated_at).toLocaleString()}</p>
        </div>

        {/* Big banner */}
        <div className={`sw-card p-6 ${problems.length === 0 ? "border-emerald-500/40" : "border-red-500/40"}`}>
          {problems.length === 0 ? (
            <div>
              <div className="font-display text-2xl font-bold text-emerald-400">All systems normal.</div>
              <div className="text-xs text-slate-400 mt-1">no current incidents or warnings</div>
            </div>
          ) : (
            <div>
              <div className="font-display text-2xl font-bold text-red-400">{problems.length} active issue{problems.length === 1 ? "" : "s"}</div>
              <div className="text-xs text-slate-400 mt-1">our team has been notified</div>
            </div>
          )}
        </div>

        {/* Counts */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            ["Backups",    data.counts.backups.total,    "text-white"],
            ["Healthy",    data.counts.backups.healthy,  "text-emerald-400"],
            ["Warning",    data.counts.backups.warning,  "text-amber-400"],
            ["Failure",    data.counts.backups.failure,  "text-red-400"],
            ["Missed",     data.counts.backups.missed,   "text-slate-300"],
          ].map(([l, v, c]) => (
            <div key={l} className="sw-card p-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{l}</div>
              <div className={`font-display text-3xl font-bold mt-1 ${c}`}>{v}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            ["Monitors", data.counts.monitors.total,    "text-white"],
            ["Up",       data.counts.monitors.up,       "text-emerald-400"],
            ["Degraded", data.counts.monitors.degraded, "text-amber-400"],
            ["Down",     data.counts.monitors.down,     "text-red-400"],
            ["Pending",  data.counts.monitors.pending,  "text-slate-300"],
          ].map(([l, v, c]) => (
            <div key={l} className="sw-card p-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{l}</div>
              <div className={`font-display text-3xl font-bold mt-1 ${c}`}>{v}</div>
            </div>
          ))}
        </div>

        {problems.length > 0 && (
          <section>
            <h2 className="font-display text-lg font-bold tracking-tight mb-3 text-red-400">Active issues</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="status-issues">
              {problems.map((it) => <StatusItemCard key={`${it._kind}-${it.id}`} item={it} />)}
            </div>
          </section>
        )}

        {healthy.length > 0 && (
          <section>
            <h2 className="font-display text-lg font-bold tracking-tight mb-3 text-slate-200">Healthy</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="status-healthy">
              {healthy.map((it) => <StatusItemCard key={`${it._kind}-${it.id}`} item={it} />)}
            </div>
          </section>
        )}

        {allBackups.length === 0 && allMons.length === 0 && (
          <div className="sw-card p-12 text-center">
            <div className="text-sm text-slate-400">No items configured yet for this client.</div>
          </div>
        )}
      </main>

      <footer className="border-t border-border py-6 text-center text-xs text-slate-500 space-y-2">
        <div>
          <a href={`/trust/${token}`} target="_blank" rel="noopener noreferrer"
            data-testid="public-status-trust-link"
            className="text-brand-cyan hover:text-white transition-colors">
            View 90-day Trust Report →
          </a>
        </div>
        <div>Powered by <span className="text-brand-cyan">SentryWatch</span></div>
      </footer>
    </div>
  );
}

function FullCenter({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background-deep text-white sw-grid-bg p-6">
      {children}
    </div>
  );
}

function StatusItemCard({ item }) {
  return (
    <div className="sw-card p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {item._kind === "backup"
            ? <HardDrives size={14} weight="duotone" className="text-brand-cyan shrink-0" />
            : <Pulse size={14} weight="duotone" className="text-brand-cyan shrink-0" />}
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{item._kind}</span>
        </div>
        <StatusBadge status={item.status} />
      </div>
      <div className="font-display text-base font-bold text-white truncate" title={item.name}>{item.name}</div>
      <div className="text-xs text-slate-400 truncate">
        {item._kind === "backup" ? `${item.vendor || "?"} · ${item.device || "—"}` : `${item.type?.toUpperCase()} · ${item.target}`}
      </div>
    </div>
  );
}
